import React, { useState, useEffect } from 'react';
import { Check, X, Users, Crown } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

interface Tecnico {
  user_id: string;
  nombre: string;
  avatar_url?: string | null;
}

import { TecnicoAsignado } from '@/contexts/OrdenesTrabajoContext';

interface TecnicosMultiSelectorProps {
  value: TecnicoAsignado[];
  onChange: (tecnicos: TecnicoAsignado[]) => void;
  disabled?: boolean;
}

export const TecnicosMultiSelector: React.FC<TecnicosMultiSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [loading, setLoading] = useState(true);
  const { perfil } = useAuth();

  useEffect(() => {
    if (perfil?.empresa_id) {
      loadTecnicos();
    }
  }, [perfil?.empresa_id]);

  const loadTecnicos = async () => {
    try {
      let query = supabase
        .from('perfiles')
        .select('user_id, nombre, avatar_url')
        .in('rol', ['tecnico', 'admin'])
        .eq('estado', 'activo')
        .order('nombre');

      if (perfil?.empresa_id) {
        query = query.eq('empresa_id', perfil.empresa_id);
      }

      const { data, error } = await query;
      if (error) throw error;
      setTecnicos(data || []);
    } catch (error) {
      console.error('Error loading tecnicos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (nombre: string) => {
    return nombre.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const isSelected = (tecnicoId: string) => {
    return value.some(t => t.tecnico_id === tecnicoId);
  };

  const getRol = (tecnicoId: string): 'principal' | 'apoyo' | null => {
    const found = value.find(t => t.tecnico_id === tecnicoId);
    return found?.rol || null;
  };

  const handleToggleTecnico = (tecnico: Tecnico) => {
    if (disabled) return;
    
    if (isSelected(tecnico.user_id)) {
      // Remove technician
      const newValue = value.filter(t => t.tecnico_id !== tecnico.user_id);
      
      // If removed the principal, promote first apoyo to principal
      if (getRol(tecnico.user_id) === 'principal' && newValue.length > 0) {
        newValue[0] = { ...newValue[0], rol: 'principal' };
      }
      
      onChange(newValue);
    } else {
      // Add technician
      const rol = value.length === 0 ? 'principal' : 'apoyo';
      onChange([...value, { 
        tecnico_id: tecnico.user_id, 
        rol,
        nombre: tecnico.nombre,
        avatar_url: tecnico.avatar_url
      }]);
    }
  };

  const handleSetPrincipal = (tecnicoId: string) => {
    if (disabled) return;
    
    const newValue = value.map(t => ({
      ...t,
      rol: (t.tecnico_id === tecnicoId ? 'principal' : 'apoyo') as 'principal' | 'apoyo'
    }));
    onChange(newValue);
  };

  const handleRemove = (tecnicoId: string) => {
    if (disabled) return;
    
    const wasPrincipal = getRol(tecnicoId) === 'principal';
    const newValue = value.filter(t => t.tecnico_id !== tecnicoId);
    
    // If removed the principal, promote first remaining to principal
    if (wasPrincipal && newValue.length > 0) {
      newValue[0] = { ...newValue[0], rol: 'principal' };
    }
    
    onChange(newValue);
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-2">Cargando técnicos...</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-muted-foreground" />
        <Label>Técnicos Asignados</Label>
        {value.length > 0 && (
          <Badge variant="secondary" className="ml-auto">
            {value.length} técnico{value.length !== 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {/* Selected Technicians Preview */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 bg-muted/50 rounded-lg">
          {value.map(asignado => {
            const tecnico = tecnicos.find(t => t.user_id === asignado.tecnico_id);
            if (!tecnico) return null;
            
            return (
              <div
                key={asignado.tecnico_id}
                className={cn(
                  "flex items-center gap-1.5 px-2 py-1 rounded-full text-sm",
                  asignado.rol === 'principal' 
                    ? "bg-primary/20 border border-primary/50" 
                    : "bg-secondary border border-border"
                )}
              >
                <Avatar className="h-5 w-5">
                  <AvatarImage src={tecnico.avatar_url || undefined} />
                  <AvatarFallback className="text-[10px]">
                    {getInitials(tecnico.nombre)}
                  </AvatarFallback>
                </Avatar>
                <span className="max-w-[100px] truncate">{tecnico.nombre.split(' ')[0]}</span>
                {asignado.rol === 'principal' && (
                  <Crown className="h-3 w-3 text-primary" />
                )}
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 ml-1 hover:bg-destructive/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(asignado.tecnico_id);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Technician List */}
      <ScrollArea className="h-[200px] border rounded-lg">
        <div className="p-2 space-y-1">
          {tecnicos.map(tecnico => {
            const selected = isSelected(tecnico.user_id);
            const rol = getRol(tecnico.user_id);
            
            return (
              <div
                key={tecnico.user_id}
                className={cn(
                  "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors",
                  selected ? "bg-primary/10" : "hover:bg-muted",
                  disabled && "opacity-50 cursor-not-allowed"
                )}
                onClick={() => handleToggleTecnico(tecnico)}
              >
                <Checkbox
                  checked={selected}
                  disabled={disabled}
                  className="pointer-events-none"
                />
                <Avatar className="h-8 w-8">
                  <AvatarImage src={tecnico.avatar_url || undefined} />
                  <AvatarFallback>{getInitials(tecnico.nombre)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{tecnico.nombre}</p>
                </div>
                {selected && (
                  <div className="flex items-center gap-1">
                    {rol === 'principal' ? (
                      <Badge variant="default" className="gap-1">
                        <Crown className="h-3 w-3" />
                        Principal
                      </Badge>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs h-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetPrincipal(tecnico.user_id);
                        }}
                        disabled={disabled}
                      >
                        Hacer principal
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>

      <p className="text-xs text-muted-foreground">
        El técnico principal es el responsable de la OT. Los técnicos de apoyo pueden registrar jornadas.
      </p>
    </div>
  );
};
